import { useRef, useState } from "react";
import ThankYou from "./ThankYou";
import emailjs from "@emailjs/browser";
import Typewriter from "typewriter-effect";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Form = ({ nameRef }) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({
    username: "",
    email: "",
    message: "",
  });

  const form = useRef();
  const emailJsUserId = process.env.REACT_APP_EMAILJS_USER_ID;
  const emailJsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const emailJsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;

  const validate = (trimmedUsername, trimmedEmail, trimmedMessage) => {
    const next = { username: "", email: "", message: "" };
    if (!trimmedUsername) next.username = "Name is required.";
    if (!trimmedEmail) next.email = "Email is required.";
    else if (!EMAIL_PATTERN.test(trimmedEmail))
      next.email = "Enter a valid email address.";
    if (!trimmedMessage) next.message = "Message is required.";
    else if (trimmedMessage.length < 10)
      next.message = "Message should be at least 10 characters.";
    setErrors(next);
    return !next.username && !next.email && !next.message;
  };

  const sendMail = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    // Sync input values with trimmed states
    setUsername(trimmedUsername);
    setEmail(trimmedEmail);
    setMessage(trimmedMessage);

    // 1. Bot spam honeypot check
    if (honeypot) {
      // Silently pretend success to confuse bots and preserve API limits
      setIsEmailSent(true);
      return;
    }

    // 2. Validate
    if (!validate(trimmedUsername, trimmedEmail, trimmedMessage)) return;

    // 3. Client-side rate-limiting / throttling (60-second cooldown)
    const lastSent = localStorage.getItem("last_form_submission");
    const now = Date.now();
    if (lastSent && now - parseInt(lastSent, 10) < 60000) {
      const remainingSeconds = Math.ceil((60000 - (now - parseInt(lastSent, 10))) / 1000);
      setSubmitError(
        `Please wait ${remainingSeconds} seconds before sending another message.`
      );
      return;
    }

    if (!emailJsUserId || !emailJsTemplateId || !emailJsServiceId) {
      setSubmitError(
        "Contact form is not configured. Email me directly at ucheogbonnak@gmail.com."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // Programmatic send prevents raw DOM data leakage and ensures clean payload
      await emailjs.send(
        emailJsServiceId,
        emailJsTemplateId,
        {
          user_name: trimmedUsername,
          user_email: trimmedEmail,
          message: trimmedMessage,
        },
        emailJsUserId
      );
      setIsEmailSent(true);
      localStorage.setItem("last_form_submission", Date.now().toString());
      setUsername("");
      setEmail("");
      setMessage("");
    } catch {
      setSubmitError(
        "Could not send your message. Please try again or email ucheogbonnak@gmail.com."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="center-div">
        <ThankYou />
      </div>
    );
  }

  return (
    <form className="my-form" ref={form} onSubmit={sendMail} noValidate>
      <div className="alt-text-yellow Message-me">
        <Typewriter
          options={{
            strings: ["Discuss a backend role or project"],
            autoStart: true,
            loop: false,
            cursor: "",
            delay: 40,
          }}
        />
      </div>

      {/* Honeypot field for spam prevention (hidden from users, filled by bots) */}
      <div style={{ display: "none" }} aria-hidden="true">
        <input
          type="text"
          name="website_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="form-div">
        <input
          type="text"
          name="user_name"
          id="name"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setErrors((s) => ({ ...s, username: "" }));
          }}
          ref={nameRef}
          autoComplete="name"
          maxLength={120}
          required
        />
        <label htmlFor="name">Name</label>
        {errors.username && (
          <small className="error_message">{errors.username}</small>
        )}
      </div>
      <div className="form-div">
        <input
          type="email"
          name="user_email"
          id="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors((s) => ({ ...s, email: "" }));
          }}
          autoComplete="email"
          maxLength={254}
          required
        />
        <label htmlFor="email">Email</label>
        {errors.email && (
          <small className="error_message">{errors.email}</small>
        )}
      </div>
      <div className="form-div">
        <textarea
          name="message"
          id="message"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setErrors((s) => ({ ...s, message: "" }));
          }}
          spellCheck
          maxLength={2000}
          required
        />
        <label htmlFor="message">Message</label>
        {errors.message && (
          <small className="error_message">{errors.message}</small>
        )}
      </div>

      {submitError && (
        <p className="error_message form-submit-error" role="alert">
          {submitError}
        </p>
      )}

      <input
        type="submit"
        disabled={isSubmitting}
        value={isSubmitting ? "Sending…" : "Send"}
        className="submit-btn"
      />
    </form>
  );
};

export default Form;
